import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import produce, { immerable } from 'immer';
import { OrderedSet } from 'immutable';

import { HdIcons } from '../../../ui/app-hd.icons';
import { HdGraphSubType, HdGraphType } from '../../domain/hd-graph.domain';

export interface IChartItemEntity {
	id: number;
	code: string;
	label: string;
	isActive: boolean;
	description: string;
	projectLabel: string;
	projectPictoUrl: string;
	icon: HdIcons | undefined;
	type: HdGraphType;
	subType: HdGraphSubType | undefined;
	isProjectPublic: boolean;
	isInSameProject: boolean;
	tags: OrderedSet<AppTagItemEntity>;
	updateDate: Date | null;
	creationDate: Date | null;
	createdBy: string;
	updatedBy: string;
	updatedByAvatar: AvatarEntity | null;
	createdByAvatar: AvatarEntity | null;
	pictogramUrl: string;
}

const entityDefaults: IChartItemEntity = {
	id: -1,
	code: '',
	label: '',
	isActive: false,
	description: '',
	projectLabel: '',
	projectPictoUrl: '',
	icon: undefined,
	type: HdGraphType.Barline,
	subType: undefined,
	isProjectPublic: false,
	isInSameProject: true,
	tags: OrderedSet(),
	updateDate: null,
	creationDate: null,
	createdBy: '',
	updatedBy: '',
	updatedByAvatar: null,
	createdByAvatar: null,
	pictogramUrl: '',
};

export class ChartItemEntity implements IChartItemEntity {
	private [immerable] = true;
	public id = entityDefaults.id;
	public code = entityDefaults.code;
	public label = entityDefaults.label;
	public isActive = entityDefaults.isActive;
	public description = entityDefaults.description;
	public projectLabel = entityDefaults.projectLabel;
	public isInSameProject = entityDefaults.isInSameProject;
	public icon = entityDefaults.icon;
	public type = entityDefaults.type;
	public subType = entityDefaults.subType;
	public isProjectPublic = entityDefaults.isProjectPublic;
	public projectPictoUrl = entityDefaults.projectPictoUrl;
	public tags = entityDefaults.tags;
	public updateDate = entityDefaults.updateDate;
	public creationDate = entityDefaults.creationDate;
	public createdBy = entityDefaults.createdBy;
	public updatedBy = entityDefaults.updatedBy;
	public updatedByAvatar = entityDefaults.updatedByAvatar;
	public createdByAvatar = entityDefaults.createdByAvatar;
	public pictogramUrl = entityDefaults.pictogramUrl;

	public static build(params: Partial<IChartItemEntity> = {}): ChartItemEntity {
		const entity = new ChartItemEntity();
		entity.id = params.id ?? entityDefaults.id;
		entity.code = params.code ?? entityDefaults.code;
		entity.label = params.label ?? entityDefaults.label;
		entity.isActive = params.isActive ?? entityDefaults.isActive;
		entity.description = params.description ?? entityDefaults.description;
		entity.projectLabel = params.projectLabel ?? entityDefaults.projectLabel;
		entity.isInSameProject =
			params.isInSameProject ?? entityDefaults.isInSameProject;
		entity.type = params.type ?? entityDefaults.type;
		entity.isProjectPublic =
			params.isProjectPublic ?? entityDefaults.isProjectPublic;
		entity.projectPictoUrl =
			params.projectPictoUrl ?? entityDefaults.projectPictoUrl;
		entity.tags = params.tags ?? entityDefaults.tags;
		entity.updateDate = params.updateDate ?? entityDefaults.updateDate;
		entity.creationDate = params.creationDate ?? entityDefaults.creationDate;
		entity.createdBy = params.createdBy ?? entityDefaults.createdBy;
		entity.updatedBy = params.updatedBy ?? entityDefaults.updatedBy;
		entity.pictogramUrl = params.pictogramUrl ?? entityDefaults.pictogramUrl;

		if (params.updatedBy) {
			entity.updatedByAvatar = AvatarEntity.build(params.updatedBy);
		}
		if (params.createdBy) {
			entity.createdByAvatar = AvatarEntity.build(params.createdBy);
		}
		return entity;
	}

	public setIcon(icon: HdIcons): ChartItemEntity {
		return produce(this, (draft: ChartItemEntity) => {
			draft.icon = icon;
		});
	}

	public setSubType(subType: HdGraphSubType): ChartItemEntity {
		return produce(this, (draft: ChartItemEntity) => {
			draft.subType = subType;
		});
	}
}
